import React, { Children, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactElement } from 'react'
import type { EmblaCarouselType } from 'embla-carousel'
import { CarouselContext } from '..'
import styles from './styles.module.css'
import { cn } from '@s4l/utility-css'
import type { DirectionKind } from '..'

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean
  nextBtnDisabled: boolean
  onPrevButtonClick: () => void
  onNextButtonClick: () => void
}

export const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollPrev()
  }, [emblaApi])

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback((emblaEventApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaEventApi.canScrollPrev())
    setNextBtnDisabled(!emblaEventApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  }
}

export interface IButtonsProps {
  children: React.ReactNode
  emblaApi?: EmblaCarouselType | undefined
  onHandleNavigation?: (direction: DirectionKind) => void
}

export const Buttons = ({ children, emblaApi, onHandleNavigation }: IButtonsProps) => {
  const { variant } = useContext(CarouselContext)
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(emblaApi)

  if (Children.count(children) !== 2) {
    console.error('children count must be 2')
    return null
  }

  const innerLeftOnClick = (child: React.ReactElement) => (event: MouseEvent) => {
    onPrevButtonClick()
    onHandleNavigation?.('prev')
    child.props?.onClick?.(event)
  }

  const innerRightOnClick = (child: React.ReactElement) => (event: MouseEvent) => {
    onNextButtonClick()
    onHandleNavigation?.('next')
    child.props?.onClick?.(event)
  }

  const [prevButton, nextButton] = Children.toArray(children).map((child, index) => {
    if (!React.isValidElement(child)) return child

    if (index === 0) return React.cloneElement(child as ReactElement<{onClick: (event: MouseEvent) => void; disabled: boolean}>, { onClick: innerLeftOnClick(child), disabled: prevBtnDisabled }) // prettier-ignore
    if (index === 1) return React.cloneElement(child as ReactElement<{onClick: (event: MouseEvent) => void; disabled: boolean}>, { onClick: innerRightOnClick(child), disabled: nextBtnDisabled }) // prettier-ignore
    return child
  })

  return (
    <div className={cn(styles.buttons, variant && styles[variant])} data-role="buttons">
      <div className={styles.prevButton} data-disabled={prevBtnDisabled} data-testid="back-cta">
        {prevButton}
      </div>
      <div className={styles.nextButton} data-disabled={nextBtnDisabled} data-testid="next-cta">
        {nextButton}
      </div>
    </div>
  )
}

Buttons.displayName = 'Carousel.Buttons'
